// Shared marketplace category model (Marketplace/Partner initiative, Phase 1). Data-driven,
// capability-aware categories used by both the employer Discover page and the Product Detail page —
// a category only appears when it has products, so new partner capabilities surface automatically.
import { Coins, HeartPulse, Building2, Boxes, Landmark, Wallet, PiggyBank, ShieldCheck, type LucideIcon } from 'lucide-react';
import type { CreditProduct } from '@/features/credit/types';
import { formatMoney } from '../payroll/lib/format';
import { categoryLabel } from './productAction';

export type BadgeVariant = 'success' | 'orange' | 'teal' | 'secondary' | 'info' | 'purple' | 'neutral';
export type MpCategory = { key: string; label: string; icon: LucideIcon; variant: BadgeVariant };

export const MP_CATEGORIES: MpCategory[] = [
  { key: 'banking', label: 'Banking', icon: Landmark, variant: 'success' },
  { key: 'payroll', label: 'Payroll & Payments', icon: Wallet, variant: 'orange' },
  { key: 'business', label: 'Business Finance', icon: Building2, variant: 'teal' },
  { key: 'employee', label: 'Employee Finance', icon: Coins, variant: 'secondary' },
  { key: 'insurance', label: 'Insurance', icon: ShieldCheck, variant: 'info' },
  { key: 'benefits', label: 'Benefits & Wellness', icon: HeartPulse, variant: 'teal' },
  { key: 'savings', label: 'Savings & Pension', icon: PiggyBank, variant: 'purple' },
  { key: 'other', label: 'Other services', icon: Boxes, variant: 'neutral' },
];

/** Map a product's category / fulfillment domain to its marketplace category. */
export function groupKeyFor(p: { category?: string; fulfillmentDomain?: string }): string {
  const c = (p.category ?? 'CREDIT').toUpperCase();
  switch (c) {
    case 'BANKING': return 'banking';
    case 'PAYROLL': case 'PAYMENTS': return 'payroll';
    case 'BUSINESS_CREDIT': case 'SME': case 'ASSET_FINANCE': return 'business';
    case 'CREDIT': case 'EMERGENCY': case 'DEVICE': case 'EDUCATION': case 'SALARY_ADVANCE': return 'employee';
    case 'INSURANCE': return 'insurance';
    case 'HEALTH': case 'BENEFIT': case 'WELLNESS': return 'benefits';
    case 'SAVINGS': case 'PENSION': return 'savings';
    default: return (p.fulfillmentDomain ?? '').toLowerCase() === 'benefits' ? 'benefits' : 'other';
  }
}

export const categoryFor = (p: CreditProduct): MpCategory =>
  MP_CATEGORIES.find((c) => c.key === groupKeyFor(p)) ?? MP_CATEGORIES[MP_CATEGORIES.length - 1];

export const isLending = (p: CreditProduct): boolean => ['employee', 'business'].includes(groupKeyFor(p));

/** The card/detail badge — the product's specific category label in its category colour. */
export function productBadge(p: CreditProduct): { label: string; variant: BadgeVariant } {
  return { label: categoryLabel(p.category ?? 'CREDIT'), variant: categoryFor(p).variant };
}

// A one-line, category-correct summary — never a fake interest rate for a non-lending product.
export function summarize(p: CreditProduct): string {
  if (isLending(p) && p.defaultAnnualRate > 0) {
    const method = p.interestMethod === 'reducing' ? 'Reducing balance' : 'Flat rate';
    const range = p.maxAmount > 0 ? ` · ${formatMoney(p.minAmount, p.currency)}–${formatMoney(p.maxAmount, p.currency)}` : '';
    return `${method} · ${p.defaultAnnualRate}% p.a.${range}`;
  }
  const key = groupKeyFor(p);
  if (key === 'insurance' || key === 'benefits') {
    const cover = p.maxAmount > 0 ? ` · cover up to ${formatMoney(p.maxAmount, p.currency)}` : '';
    return `${categoryLabel(p.category ?? 'CREDIT')}${cover}`;
  }
  if (p.maxAmount > 0) return `${categoryLabel(p.category ?? 'CREDIT')} · up to ${formatMoney(p.maxAmount, p.currency)}`;
  return categoryLabel(p.category ?? 'CREDIT');
}
